// FE-84 (21.10.2026): Services.js bölündü — ortak importlar, interceptor kurulumu, get-user-infos önbelleği ve
// teklif/talep statü yardımcıları burada. Metotlar services/alanlar/*.js; Services.js hepsini birleştirir.
import axios from "axios";
import { installRealtimeSync } from "../utils/realtimeSync";
import { installApiOturum } from "../utils/apiOturum";

// Tüm mutasyon isteklerini socket üzerinden ilgili kullanıcılara yayınlayan
// merkezi interceptor'ı bir kez kur (idempotent).
installRealtimeSync();
// FE-77: ortak oturum düşmesi + bağlantı hatası olayları (utils/apiOturum.js).
installApiOturum();

// FE-35: get-user-infos aynı ekranda Header, Sidebar, AuthContext, ChatContext ve sayfanın kendisi tarafından
// aynı anda çağrılıyordu (sayfa açılışında 3–6 özdeş istek). Aynı token için uçuştaki istek paylaşılır, başarılı
// sonuç USER_INFOS_TTL_MS boyunca yeniden kullanılır. Veri değiştiren her çağrı (flow/ altında get- ile başlamayan)
// gidişte ve dönüşte önbelleği boşaltır; socket'ten gelen status:update da boşaltır (ChatContext).
export const USER_INFOS_TTL_MS = 3000;
// Nesne yerinde güncellenir (import edilen bağlamaya yeniden atama yapılamaz; alanlar/kullanici.js kullanır).
export const userInfosCache = { token: "", at: 0, promise: null };
export function invalidateUserInfosCache() {
  Object.assign(userInfosCache, { token: "", at: 0, promise: null });
}
export function isMutationRequest(config) {
  const ep = String(config?.url || "").split("/flow/")[1] || "";
  return ep !== "" && !ep.startsWith("get-");
}
axios.interceptors.request.use((config) => {
  if (isMutationRequest(config)) invalidateUserInfosCache();
  return config;
});
axios.interceptors.response.use(
  (res) => { if (isMutationRequest(res?.config)) invalidateUserInfosCache(); return res; },
  (err) => { if (isMutationRequest(err?.config)) invalidateUserInfosCache(); return Promise.reject(err); },
);

export const REMOVED_OFFER_STATUSES = new Set(["0", "2", "-1"]);
export const OFFER_WAITING_CUSTOMER_RESPONSE_STATUS = "4";
export const OFFER_TO_SEND_STATUSES = new Set(["3", "5"]);
export const REQUEST_TO_SEND_OFFER_STATUS = "1";
export const REQUEST_OFFERED_STATUS = "4";

export function normalizeServiceId(value) {
  if (value === null || value === undefined || typeof value === "object") return "";
  return String(value).trim();
}

export function pickOfferUserId(offer) {
  const candidates = [
    offer?.userID,
    offer?.userId,
    offer?.customerID,
    offer?.customerId,
    offer?.user?.userID,
    offer?.user?.userId,
    offer?.customer?.userID,
    offer?.customer?.userId,
    offer?.customer?.customerID,
    offer?.customer?.customerId,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeServiceId(candidate);
    if (normalized) return normalized;
  }

  return "";
}

export function isOfferActive(offer) {
  const status = String(offer?.status ?? "").trim();
  return !REMOVED_OFFER_STATUSES.has(status);
}

export function resolveRequestStatusAfterOfferRemoval(activeOffers, fallbackStatus, currentRequestStatus) {
  const currentStatus = normalizeServiceId(currentRequestStatus);
  if (currentStatus === REQUEST_OFFERED_STATUS) return null;
  if (!activeOffers.length) return fallbackStatus;

  const activeOfferStatuses = activeOffers.map((offer) => String(offer?.status ?? "").trim());
  const hasWaitingOffer = activeOfferStatuses.includes(OFFER_WAITING_CUSTOMER_RESPONSE_STATUS);
  const hasToSendOffer = activeOfferStatuses.some((status) => OFFER_TO_SEND_STATUSES.has(status));

  if (hasWaitingOffer && hasToSendOffer) {
    return currentStatus === REQUEST_TO_SEND_OFFER_STATUS ? null : REQUEST_TO_SEND_OFFER_STATUS;
  }

  if (hasWaitingOffer) {
    return currentStatus === REQUEST_OFFERED_STATUS ? null : REQUEST_OFFERED_STATUS;
  }

  return null;
}

// --- REDDETME'ye özel talep statüsü türetme (SİLME akışını etkilemez) ---
// Yalnızca teklif REDDEDİLDİĞİNDE (silinmediğinde) kullanılır; reject akışı Services'e
// `rejectReason` geçtiğinde devreye girer. Kurallar (kullanıcı direktifi):
//   • Tek teklif (reddedilen) → reddetme sebebine göre statü: "Anlaşılamadı" → "9",
//     diğer tüm sebepler → "2" (İptal Edildi).
//   • Çok teklif, HİÇ kabul edilmiş yoksa → EN SON eklenen teklifin durumundan türet.
//   • Kabul edilmiş/onay bekleyen teklif (status "1" veya "7") varsa DOKUNMA — kabul akışı korunur.
export const REQUEST_STATUS_REJECTED_CANCELLED = "2"; // İptal Edildi
export const REQUEST_STATUS_COULD_NOT_AGREE = "9";    // Anlaşılamadı
export const OFFER_ACCEPTED_STATUSES = new Set(["1", "7"]); // Kabul Edildi / Onay Bekliyor (kabul akışı)

export function rejectReasonToRequestStatus(reason) {
  return String(reason ?? "").includes("could-not-agree")
    ? REQUEST_STATUS_COULD_NOT_AGREE
    : REQUEST_STATUS_REJECTED_CANCELLED;
}

// Teklifin ANLAMLI bir reddetme sebebi taşıyıp taşımadığı. Gönderilmeden silinen tekliflerde
// cancelReason "internal-hidden-unsent-offer" (sebepsiz) olabilir → bu sebep SAYILMAZ.
export function offerHasRejectReason(cancelReason) {
  let raw = String(cancelReason ?? "").trim();
  if (!raw) return false;
  if (raw === "internal-hidden-unsent-offer") return false;
  if (raw.startsWith("internal-hidden-unsent-offer:")) raw = raw.slice("internal-hidden-unsent-offer:".length).trim();
  return raw.length > 0;
}

// Teklifin red/kaldırılma zamanı (yoksa oluşturma zamanı) — "en son reddedilen"i bulmak için.
export function offerRejectTs(offer) {
  const raw = offer?.cancelledDateTime ?? offer?.cancelled_date_time ?? offer?.createdDateTime ?? offer?.id ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

// Teklif REDDEDİLDİĞİNDE veya SİLİNDİĞİNDE talep statüsünü türetir (reject + delete ortak):
//   • Kabul/onay bekleyen teklif varsa → dokunma (kabul akışı korunur).
//   • AKTİF (reddedilmemiş) teklif varsa → talep aktif teklifi yansıtır (Teklif Verildi/Gönderilecek).
//   • HEPSİ reddedilmiş/silinmişse → EN SON reddedilen SEBEPLİ teklifin sebebine göre (Anlaşılamadı/
//     İptal Edildi). Sebepsiz silme olsa bile önceki sebepli red varsa ona düşer.
//   • Hiç sebepli red yoksa → eski (fallback) davranış (nakliyatta "Teklif Toplanacak" vb.).
export function resolveRequestStatusAfterOfferReject(requestOffers, rejectReason, fallbackStatus, currentRequestStatus) {
  const offers = Array.isArray(requestOffers) ? requestOffers : [];
  const activeOffers = offers.filter((o) => isOfferActive(o));

  // Kabul akışını koru.
  if (offers.some((o) => OFFER_ACCEPTED_STATUSES.has(normalizeServiceId(o?.status)))) return null;

  if (activeOffers.length > 0) {
    const activeStatuses = activeOffers.map((o) => normalizeServiceId(o?.status));
    if (activeStatuses.includes(OFFER_WAITING_CUSTOMER_RESPONSE_STATUS)) return REQUEST_OFFERED_STATUS;   // Teklif Verildi
    if (activeStatuses.some((s) => OFFER_TO_SEND_STATUSES.has(s))) return REQUEST_TO_SEND_OFFER_STATUS;    // Teklif Gönderilecek
    return resolveRequestStatusAfterOfferRemoval(activeOffers, fallbackStatus, currentRequestStatus);      // diğer aktif durumlar → eski davranış
  }

  // Aktif teklif yok → EN SON reddedilen (SEBEPLİ) teklifin sebebine göre.
  const removedWithReason = offers
    .filter((o) => REMOVED_OFFER_STATUSES.has(normalizeServiceId(o?.status)) && offerHasRejectReason(o?.cancelReason))
    .sort((a, b) => offerRejectTs(b) - offerRejectTs(a));
  if (removedWithReason.length) return rejectReasonToRequestStatus(removedWithReason[0].cancelReason);
  if (offerHasRejectReason(rejectReason)) return rejectReasonToRequestStatus(rejectReason);

  // Sebepli hiçbir red yok → eski (fallback) davranış.
  return resolveRequestStatusAfterOfferRemoval(activeOffers, fallbackStatus, currentRequestStatus);
}

// eslint-disable-next-line
